'use client';

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CheckIcon } from '@/components/icons';
import type { CountryOption } from '../countries';

export type CountryPickerMode = 'dial' | 'name';

type Props = {
  listId: string;
  mode: CountryPickerMode;
  listAriaLabel: string;
  detected: CountryOption | null;
  options: CountryOption[];
  value: string;
  activeIso: string | null;
  listRef: React.RefObject<HTMLDivElement | null>;
  onSelect: (iso2: string) => void;
};

const sectionLabelSx = {
  px: 1.5,
  pt: 1,
  pb: 0.5,
  fontSize: 11,
  letterSpacing: '0.06em',
  color: 'text.secondary',
};

export function optionDomId(listId: string, iso2: string): string {
  return `${listId}-option-${iso2}`;
}

export function IntakeCountryPickerList({
  listId,
  mode,
  listAriaLabel,
  detected,
  options,
  value,
  activeIso,
  listRef,
  onSelect,
}: Props) {
  const selectedIso = value.toUpperCase();
  const showDial = mode === 'dial';

  return (
    <Box
      ref={listRef}
      id={listId}
      role="listbox"
      aria-label={listAriaLabel}
      sx={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}
    >
      {detected && (
        <Box role="group" aria-label="Detected">
          <Typography sx={sectionLabelSx} aria-hidden>
            DETECTED
          </Typography>
          <CountryRow
            listId={listId}
            option={detected}
            selected={detected.iso2 === selectedIso}
            active={detected.iso2 === activeIso}
            showDial={showDial}
            onSelect={onSelect}
          />
        </Box>
      )}
      {options.length > 0 && (
        <Box role="group" aria-label="All countries">
          <Typography
            sx={{
              ...sectionLabelSx,
              borderTop: detected ? 1 : 0,
              borderColor: 'divider',
            }}
            aria-hidden
          >
            ALL COUNTRIES
          </Typography>
          {options.map(option => (
            <CountryRow
              key={option.iso2}
              listId={listId}
              option={option}
              selected={option.iso2 === selectedIso}
              active={option.iso2 === activeIso}
              showDial={showDial}
              onSelect={onSelect}
            />
          ))}
        </Box>
      )}
      {!detected && options.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
          No countries found
        </Typography>
      )}
    </Box>
  );
}

function CountryRow({
  listId,
  option,
  selected,
  active,
  showDial,
  onSelect,
}: {
  listId: string;
  option: CountryOption;
  selected: boolean;
  active: boolean;
  showDial: boolean;
  onSelect: (iso2: string) => void;
}) {
  return (
    <Box
      id={optionDomId(listId, option.iso2)}
      role="option"
      aria-selected={selected}
      tabIndex={-1}
      onClick={() => onSelect(option.iso2)}
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        border: 0,
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
        color: 'text.primary',
        outline: 'none',
        bgcolor: theme => {
          if (active) return alpha(theme.palette.primary.main, 0.16);
          if (selected) return alpha(theme.palette.primary.main, 0.08);
          return 'transparent';
        },
        boxShadow: theme =>
          active ? `inset 0 0 0 2px ${theme.palette.primary.main}` : 'none',
        '&:hover': {
          bgcolor: theme =>
            alpha(theme.palette.primary.main, active || selected ? 0.16 : 0.04),
        },
      }}
    >
      <Typography variant="body2">{option.name}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        {showDial && (
          <Typography variant="body2" color="text.secondary">
            {option.dialCode}
          </Typography>
        )}
        {selected && <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
